/**
 * MasterclassEmailGate — public lead-magnet capture screen. Fires after
 * chapter 1 of a public masterclass preview. Submits {name, email,
 * masterclass_key} to /api/capture-masterclass-lead and on success calls
 * onUnlock so the reader proceeds to the gated chapter.
 *
 * Captured state is persisted to localStorage so a coach who returns
 * later (same browser) doesn't have to re-fill. Email submission is
 * dedup'd server-side on (email, masterclass_key).
 *
 * The gate intentionally renders as a full-screen overlay inside the
 * masterclass reader so the coach can't scroll past it or click off.
 * Two buttons: "Unlock the rest" (primary) and a small "Maybe later"
 * that just returns to chapter 1.
 */
import React, { useState } from "react";
import { Lock, Check, X as XIcon } from "lucide-react";
import { GOLD } from "../constants/theme";
import { logEvent } from "../utils/analytics";
import { metaTrack } from "../../utils/metaPixel";

const localKey = (moduleKey) => `bam_mc_lead_${moduleKey}`;

export function hasCapturedLead(moduleKey) {
  try { return localStorage.getItem(localKey(moduleKey)) === "1"; } catch { return false; }
}
export function markLeadCaptured(moduleKey) {
  try { localStorage.setItem(localKey(moduleKey), "1"); } catch {}
}

export function MasterclassEmailGate({ moduleKey, moduleTitle, source, onUnlock, onBack, C }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // Force coach/player selection. The lead-magnet link gets shared
  // on socials where some respondents will be players or parents.
  // Coleman needs to filter players out of the coach email list and
  // into a separate virtual-academy outreach list — that split lives
  // on the server side via the `role` column.
  const [role, setRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    if (!role) {
      setError("Pick one: are you a coach / trainer, or a player?");
      return;
    }
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    logEvent("masterclass_lead_submit", { moduleKey, source, role });
    try {
      const resp = await fetch("/api/capture-masterclass-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmedEmail,
          name: trimmedName || null,
          masterclass_key: moduleKey,
          source: source || null,
          role,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.error || "Something went wrong. Try again.");
        setSubmitting(false);
        return;
      }
      markLeadCaptured(moduleKey);
      logEvent("masterclass_lead_captured", { moduleKey, source });
      // Meta Pixel Lead conversion (lead-magnet campaign). Web-only; no-op
      // in the native app / for opted-out visitors.
      metaTrack("Lead", { content_name: moduleTitle || moduleKey, content_category: role });
      onUnlock && onUnlock();
    } catch (err) {
      setError("Network error. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 5000,
      background: "rgba(8,8,8,0.92)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "16px",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        maxWidth: 480, width: "100%",
        background: "linear-gradient(180deg, #111111, #0A0A0A)",
        border: `1px solid ${GOLD}55`,
        borderRadius: 18,
        boxShadow: `0 30px 80px rgba(0,0,0,0.75), inset 0 1px 0 ${GOLD}22`,
        padding: "28px 24px 24px",
        position: "relative",
      }}>
        {/* Top gold accent rule */}
        <div style={{
          position: "absolute", top: 0, left: 24, right: 24, height: 2,
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          opacity: 0.6,
        }}/>
        {/* Close (small back exit) */}
        {onBack && (
          <button onClick={onBack} aria-label="Close" style={{
            position: "absolute", top: 12, right: 12,
            background: "transparent", border: "none", cursor: "pointer",
            padding: 6, display: "flex", alignItems: "center", justifyContent: "center",
            color: C?.textDim || "#888",
          }}>
            <XIcon size={16}/>
          </button>
        )}

        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: `${GOLD}18`, border: `1px solid ${GOLD}55`,
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 16,
        }}>
          <Lock size={20} color={GOLD}/>
        </div>

        <div style={{
          fontSize: 10, fontWeight: 800, color: GOLD,
          letterSpacing: 1.6, textTransform: "uppercase", marginBottom: 6,
        }}>You're previewing</div>
        <h2 style={{
          fontSize: 22, fontWeight: 800, color: "#F5F0E8",
          margin: "0 0 10px", lineHeight: 1.25, letterSpacing: -0.2,
        }}>Unlock the rest of "{moduleTitle}"</h2>
        <p style={{
          fontSize: 14, lineHeight: 1.6, color: "#A09A8B",
          margin: "0 0 18px",
        }}>
          Drop your email and you'll get the full masterclass instantly. No
          account needed for the read. You'll also get the occasional note
          when there's a new masterclass worth your time.
        </p>

        <form onSubmit={submit}>
          {/* Role selector — required pick. Two big chips that read
              like a question, not a form field. */}
          <div style={{
            fontSize: 11, fontWeight: 700, color: "#A09A8B",
            letterSpacing: 0.4, marginBottom: 8,
          }}>I'm a…</div>
          <div style={{display: "flex", gap: 8, marginBottom: 14}}>
            <RoleChip selected={role === "coach"} onClick={() => setRole("coach")}>
              Coach / Trainer
            </RoleChip>
            <RoleChip selected={role === "player"} onClick={() => setRole("player")}>
              Player
            </RoleChip>
          </div>

          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name (optional)"
            autoComplete="given-name"
            style={inputStyle}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="email"
            required
            style={{...inputStyle, marginTop: 10}}
          />
          {error && (
            <div style={{
              marginTop: 10, fontSize: 12, color: "#F87171",
              padding: "6px 10px",
              background: "rgba(248,113,113,0.08)",
              border: "1px solid rgba(248,113,113,0.25)",
              borderRadius: 6,
            }}>{error}</div>
          )}
          <button type="submit" disabled={submitting}
            style={{
              marginTop: 14, width: "100%",
              padding: "13px 0", borderRadius: 10,
              background: GOLD, color: "#0A0A0A",
              fontSize: 14, fontWeight: 800, letterSpacing: 0.3,
              border: "none", cursor: submitting ? "default" : "pointer",
              boxShadow: `0 6px 18px ${GOLD}55`,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              opacity: submitting ? 0.7 : 1,
              fontFamily: "'DM Sans', sans-serif",
            }}>
            {submitting ? "Unlocking…" : (<><Check size={14} strokeWidth={3}/> Unlock the rest</>)}
          </button>
        </form>

        {onBack && (
          <div style={{textAlign: "center", marginTop: 12}}>
            <button onClick={onBack}
              style={{
                background: "transparent", border: "none",
                fontSize: 12, color: "#6C6658", cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}>
 Maybe later: keep reading chapter 1
            </button>
          </div>
        )}

        <div style={{
          marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.06)",
          fontSize: 10, color: "#4C4740", lineHeight: 1.5, textAlign: "center",
        }}>
          One email per coach. We don't share with anyone. Unsubscribe anytime.
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 9,
  color: "#F5F0E8",
  fontSize: 14,
  outline: "none",
  fontFamily: "'DM Sans', sans-serif",
  boxSizing: "border-box",
};

function RoleChip({ children, selected, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: 1,
      padding: "11px 14px",
      borderRadius: 9,
      border: `1px solid ${selected ? GOLD : "rgba(255,255,255,0.10)"}`,
      background: selected ? `${GOLD}1A` : "rgba(255,255,255,0.04)",
      color: selected ? GOLD : "#F5F0E8",
      fontSize: 13,
      fontWeight: selected ? 700 : 500,
      cursor: "pointer",
      fontFamily: "'DM Sans', sans-serif",
      transition: "all .15s",
      letterSpacing: 0.2,
    }}>
      {children}
    </button>
  );
}

export default MasterclassEmailGate;
